import { useEffect, useMemo, useState } from "react";
import { CalendarCheck, RefreshCw, Settings2, Search, History as HistoryIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiErrorMessage, formatTanggalID } from "@/lib/api";
import { HistoryPengajuanDialog } from "./HistoryPengajuanDialog";

export default function AdminSaldoCuti() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kuotaInput, setKuotaInput] = useState("12");
  const [savingKuota, setSavingKuota] = useState(false);
  const [q, setQ] = useState("");
  const [historyRow, setHistoryRow] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [saldo, cfg] = await Promise.all([
        api.get("/cuti/saldo/all"),
        api.get("/settings/cuti"),
      ]);
      setData(saldo.data);
      setKuotaInput(String(cfg.data.kuota_default_tahunan));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function saveKuota() {
    const v = parseInt(kuotaInput, 10);
    if (isNaN(v) || v <= 0 || v > 60) {
      toast.error("Kuota default harus 1-60 hari");
      return;
    }
    setSavingKuota(true);
    try {
      await api.put("/settings/cuti", { kuota_default_tahunan: v });
      toast.success("Kuota default disimpan");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSavingKuota(false);
    }
  }

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!q.trim()) return data.rows;
    const s = q.toLowerCase();
    return data.rows.filter(
      (r) =>
        r.nama?.toLowerCase().includes(s) ||
        r.npp?.toLowerCase().includes(s) ||
        r.unit_nama?.toLowerCase().includes(s)
    );
  }, [data, q]);

  const stat = useMemo(() => {
    if (!data) return { total: 0, terpakai: 0, tersisa: 0 };
    return data.rows.reduce(
      (acc, r) => ({
        total: acc.total + (r.kuota_tahunan || 0),
        terpakai: acc.terpakai + (r.terpakai || 0),
        tersisa: acc.tersisa + (r.tersisa || 0),
      }),
      { total: 0, terpakai: 0, tersisa: 0 }
    );
  }, [data]);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="page-saldo-cuti">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            Administrasi
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Saldo Cuti Tahunan
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Rekap saldo cuti semua pegawai tahun {data?.year || "…"}. Diperbarui otomatis saat pengajuan Cuti disetujui.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="saldo-refresh-btn">
          <RefreshCw size={14} /> Muat Ulang
        </Button>
      </div>

      {/* Settings + search */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-slate-500 mb-3">
            <Search size={12} /> Cari Pegawai
          </div>
          <Input
            placeholder="Cari nama, NPP, atau unit…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="saldo-search"
          />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-slate-500 mb-3">
            <Settings2 size={12} /> Default Kuota Global
          </div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
            Hari / Tahun
          </Label>
          <div className="flex gap-2 mt-1.5">
            <Input
              type="number"
              min="1"
              max="60"
              value={kuotaInput}
              onChange={(e) => setKuotaInput(e.target.value)}
              data-testid="input-default-kuota"
            />
            <Button
              onClick={saveKuota}
              disabled={savingKuota}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="save-default-kuota-btn"
            >
              Simpan
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Berlaku untuk pegawai yang tidak punya override individu.
          </p>
        </div>
      </div>

      {/* Aggregate stats */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatBox label="Total Pegawai" value={data?.rows?.length ?? 0} tone="slate" testId="stat-saldo-count" />
        <StatBox label="Total Kuota" value={stat.total} tone="slate" testId="stat-saldo-kuota" />
        <StatBox label="Total Terpakai" value={stat.terpakai} tone="emerald" testId="stat-saldo-terpakai" />
        <StatBox label="Total Tersisa" value={stat.tersisa} tone="amber" testId="stat-saldo-tersisa" />
      </div>

      {/* Table */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="sticky top-0">
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Pegawai</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Unit</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Awal Bergabung</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Kuota</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Terpakai</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Menunggu</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Tersisa</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Sumber Kuota</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-14">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <CalendarCheck size={22} />
                    </div>
                    <div className="mt-3 font-semibold text-slate-700">Tidak ada pegawai cocok filter</div>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => {
                  const pct = r.kuota_tahunan > 0 ? (r.terpakai / r.kuota_tahunan) * 100 : 0;
                  const habis = r.tersisa === 0;
                  return (
                    <TableRow key={r.user_id} data-testid={`saldo-row-${r.user_id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                      <TableCell>
                        <div className="font-semibold text-slate-900">{r.nama}</div>
                        <div className="text-xs text-slate-500 font-mono-clock">{r.npp}</div>
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">{r.unit_nama}</TableCell>
                      <TableCell className="text-slate-600 text-sm whitespace-nowrap" data-testid={`saldo-awal-bergabung-${r.user_id}`}>
                        {r.awal_bergabung ? formatTanggalID(r.awal_bergabung) : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono-clock font-bold text-slate-900">{r.kuota_tahunan}</TableCell>
                      <TableCell className="text-right font-mono-clock text-emerald-700">{r.terpakai}</TableCell>
                      <TableCell className="text-right font-mono-clock text-amber-700">{r.tertahan_menunggu}</TableCell>
                      <TableCell className="text-right">
                        <div className={`font-mono-clock font-bold ${habis ? "text-rose-700" : "text-teal-800"}`}>
                          {r.tersisa}
                        </div>
                        <div className="h-1 rounded-full bg-slate-100 overflow-hidden mt-1 w-20 ml-auto">
                          <div
                            className={`h-full ${habis ? "bg-rose-500" : "bg-teal-600"}`}
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        {r.override_pegawai != null ? (
                          <span className="text-[10px] font-semibold text-teal-800 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5">
                            Override
                          </span>
                        ) : (
                          <span className="text-[10px] font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">
                            Default
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5 h-8 text-xs font-semibold border-teal-200 text-teal-800 hover:bg-teal-50"
                          onClick={() => setHistoryRow(r)}
                          data-testid={`saldo-history-btn-${r.user_id}`}
                        >
                          <HistoryIcon size={13} /> History
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <p className="text-[11px] text-slate-500 mt-3">
        Kuota individu diatur per pegawai di menu <b>Master Pegawai</b>. Kolom Tersisa sudah memperhitungkan
        pengajuan yang masih Menunggu supaya kuota tidak melebihi batas.
      </p>

      {/* History pengajuan pegawai */}
      <HistoryPengajuanDialog
        pegawai={historyRow}
        open={!!historyRow}
        onClose={() => setHistoryRow(null)}
      />
    </div>
  );
}

function StatBox({ label, value, tone, testId }) {
  const toneMap = {
    slate: "border-slate-200 text-slate-900",
    emerald: "border-emerald-200 text-emerald-800",
    amber: "border-amber-200 text-amber-800",
  };
  return (
    <div className={`rounded-xl border bg-white p-5 ${toneMap[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
        {label}
      </div>
      <div className="mt-1 font-mono-clock font-bold text-3xl leading-tight">{value}</div>
    </div>
  );
}
